/**
 * Shared auth-token holder and acquisition helper for all API client modules.
 *
 * Pages/contexts call getToken(instance, account) to obtain an Entra access
 * token; the helper acquires it (silent first, interactive redirect fallback),
 * stores it via setAuthToken(), and returns it. Every API module imports
 * getAuthHeaders() to attach it as a Bearer on outgoing requests.
 *
 * Module-level singleton: there's one token at a time, refreshed by whichever
 * page is currently loaded. This works because pages re-acquire on mount,
 * and the token is valid for ~1 hour.
 */

import type { IPublicClientApplication, AccountInfo } from "@azure/msal-browser";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { apiRequest } from "../auth/msalConfig";

let authToken: string | null = null;

/**
 * Session key holding the timestamp of the last interactive re-auth redirect,
 * used to prevent an infinite redirect loop if re-auth keeps failing.
 */
const REAUTH_GUARD_KEY = "ar_reauth_attempted_at";

export function setAuthToken(token: string): void {
  authToken = token;
}

export function getAuthHeaders(): Record<string, string> {
  return authToken ? { Authorization: `Bearer ${authToken}` } : {};
}

/**
 * Acquire an API access token for the given account.
 *
 * Tries acquireTokenSilent first (cheap, cached). If MSAL reports that
 * interaction is required — InteractionRequiredAuthError, raised when the
 * refresh token has expired, consent was revoked, or a conditional-access
 * policy changed — it falls back to an interactive REDIRECT
 * (acquireTokenRedirect).
 *
 * A popup fallback (acquireTokenPopup) was used previously, but popup auth is
 * broken under @azure/msal-browser 5.10.x (see auth/msalConfig.ts, finding F4)
 * and is additionally blocked by the browser when invoked without a user
 * gesture — e.g. from a mount effect. The net effect was that whenever a user's
 * token aged out (typically overnight), silent acquisition failed, the popup
 * fallback never completed, and the user was left stuck on "Couldn't load your
 * portal access" — a plain refresh replayed the same stale state, and only a
 * manual sign-out/sign-in recovered (OS-1683 F5, reopened). Redirect is this
 * app's proven interactive flow (login already uses loginRedirect).
 *
 * A short-lived sessionStorage guard (REAUTH_GUARD_KEY) ensures we only redirect
 * once per ~2 minutes; if re-auth completes but a token still can't be acquired
 * silently, the error is rethrown so the caller can show a real message instead
 * of bouncing through Entra forever.
 *
 * On success the acquired token is stored via setAuthToken() and returned, so
 * callers can use it immediately. On the redirect path this function does not
 * return (the browser navigates away to Entra).
 *
 * Errors other than InteractionRequiredAuthError propagate to the caller, which
 * surfaces them in its own UI.
 */
export async function getToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<string> {
  try {
    const resp = await instance.acquireTokenSilent({ ...apiRequest, account });
    setAuthToken(resp.accessToken);
    // Fresh token acquired — reset the redirect loop guard.
    sessionStorage.removeItem(REAUTH_GUARD_KEY);
    return resp.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const lastAttempt = Number(sessionStorage.getItem(REAUTH_GUARD_KEY) ?? 0);
      if (Date.now() - lastAttempt < 120_000) {
        // We already redirected very recently and still can't get a token —
        // don't loop; let the caller surface a real error message.
        throw err;
      }
      sessionStorage.setItem(REAUTH_GUARD_KEY, String(Date.now()));
      await instance.acquireTokenRedirect({ ...apiRequest, account });
      // acquireTokenRedirect navigates the page to Entra; code below never runs.
      // Return a never-resolving promise so callers don't fall through and call
      // the API without a token during the brief page-unload window.
      return new Promise<string>(() => {});
    }
    throw err;
  }
}
